import {
  Component,
  OnInit,
  Inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { AuditItemModel } from '../audit-item.model';
import { UtilsService } from 'src/app/shared/utils.service';
import { AuditItemsService } from '../audit-items.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RBACService } from 'src/app/shared/rbac.service';

@Component({
  selector: 'app-audit-items-template-edit',
  templateUrl: './audit-items-template-edit.component.html',
  styleUrls: ['./audit-items-template-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AuditItemsTemplateEditComponent implements OnInit {
  /**
   * Is the form being submitted?
   */
  inProgress = false;

  // RBAC permission flags.
  canEdit: boolean = false;
  canCreate: boolean = false;
  canDelete: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public auditItem: AuditItemModel,
    private util: UtilsService,
    private auditItemService: AuditItemsService,
    private utils: UtilsService,
    private dialogRef: MatDialogRef<AuditItemsTemplateEditComponent>,
    private rbac: RBACService
  ) {}

  ngOnInit() {
    // Determine if editing existing question or creating new.
    const isEdit = !isNaN(this.auditItem.id);
    const requiredPermission = isEdit
      ? RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_EDIT
        )
      : RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_CREATE
        );

    if (!this.rbac.userCanAccess([requiredPermission], true)) {
      const action = isEdit ? 'edit' : 'create';
      this.rbac.showGenericAccessDeniedMessage(
        `You do not have permission to ${action} inspection/audit questions.`
      );
      this.dialogRef.close();
      return;
    }

    // Resolve permission flags.
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canDelete =
      isEdit &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      );
  }

  onSubmit() {
    if (this.inProgress) {
      return;
    }

    // Layer 2 guard: check correct permission based on create vs edit.
    const isEdit = !isNaN(this.auditItem.id);
    if (isEdit) {
      if (!this.canEdit) {
        this.rbac.showGenericAccessDeniedMessage(
          'You do not have permission to edit inspection/audit questions.'
        );
        return;
      }
    } else {
      if (!this.canCreate) {
        this.rbac.showGenericAccessDeniedMessage(
          'You do not have permission to create inspection/audit questions.'
        );
        return;
      }
    }

    this.inProgress = true;
    isNaN(this.auditItem.id) ? this.create() : this.update();
  }

  delete() {
    // Layer 2 guard: check delete permission.
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete inspection/audit questions.'
      );
      return;
    }

    this.utils.showModal(
      'Remove Inspection/Audit Question',
      `Are you sure you want to remove the "${
        this.auditItem.name
      }" Inspection/Audit question?`,
      () => {
        this.auditItemService
          .remove([this.auditItem.id])
          .subscribe(() =>
            this.onSuccess(
              `The "${
                this.auditItem.name
              }" Inspection/Audit question was removed.`
            )
          );
      }
    );
  }

  private update() {
    this.auditItemService.put(this.auditItem).subscribe({
      next: () =>
        this.onSuccess(
          `The "${this.auditItem.name}" Inspection/Audit question was updated.`
        ),
      error: () => (this.inProgress = false),
      complete: () => (this.inProgress = false)
    });
  }

  private create() {
    this.auditItemService.post(this.auditItem).subscribe({
      next: () =>
        this.onSuccess(
          `The "${this.auditItem.name}" Inspection/Audit question was created.`
        ),
      error: () => (this.inProgress = false),
      complete: () => (this.inProgress = false)
    });
  }

  private onSuccess(message: string) {
    this.util.showToast(message);
    this.dialogRef.close(true);
  }
}
